import { Typography, IconButton } from "@mui/material";
import { useState, useRef, DragEvent } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUpload,
  faFile,
  faPencil,
  faImage,
  faFilePdf,
  faFileWord,
  faFileExcel,
  faTimesCircle,
} from "@fortawesome/free-solid-svg-icons";
import { useLanguage } from "../../context/LanguageContext";
import styles from "./FileUpload.module.css";
import {
  validateFile,
  createImagePreview,
  formatFileSize,
  getFileIconType,
  FileValidationOptions,
  DEFAULT_FILE_VALIDATION,
} from "../../utils/fileValidation";

interface FileUploadProps {
  value: File | null;
  onChange: (file: File | null) => void;
  error?: string;
  touched?: boolean;
  accept?: string;
  label?: string;
  validationOptions?: FileValidationOptions;
  onValidationError?: (error: string) => void;
  disabled?: boolean;
}

const getFileIcon = (iconType: string) => {
  const iconMap: Record<string, any> = {
    image: faImage,
    pdf: faFilePdf,
    word: faFileWord,
    excel: faFileExcel,
    file: faFile,
  };
  return iconMap[iconType] || faFile;
};

export default function FileUpload({
  value,
  onChange,
  error,
  touched,
  accept = "image/*",
  label = "uploadFile",
  validationOptions = DEFAULT_FILE_VALIDATION,
  onValidationError,
}: FileUploadProps) {
  const { t } = useLanguage();
  const [isDragging, setIsDragging] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFile = async (file: File) => {
    const validation = validateFile(file, validationOptions, t);

    if (!validation.valid) {
      setValidationError(validation.error || "");
      if (onValidationError) {
        onValidationError(validation.error || "");
      }
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    setValidationError("");
    onChange(file);

    if (file.type.startsWith("image/")) {
      try {
        const previewUrl = await createImagePreview(file);
        setPreview(previewUrl);
      } catch (err) {
        setValidationError(t("errorReadingFile"));
        console.error("Error creating preview:", err);
      }
    } else {
      setPreview(null);
    }
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleClick = () => fileInputRef.current?.click();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
    setPreview(null);
    setValidationError("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const isImage = value?.type?.startsWith("image/") || false;
  const displayError = validationError || (touched && error);
  const iconType = value ? getFileIconType(value.name) : "file";

  return (
    <div style={{ width: "100%" }}>
      {label && (
        <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
          {t(label)}
        </Typography>
      )}

      <div
        className={`${styles.uploadBox} ${isDragging ? styles.dragging : ""} ${displayError ? styles.error : ""
          }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          onChange={handleFileChange}
          style={{ display: "none" }}
        />

        {!value ? (
          <div className={styles.emptyState}>
            <FontAwesomeIcon icon={faUpload} className={styles.uploadIcon} />
            <Typography variant="body1" sx={{ mt: 2, fontWeight: 500 }}>
              {t("dragOrUpload")}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.5 }}
            >
              {t("clickToSelect")}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 1 }}
            >
              {t("maxFileSize")}: {formatFileSize(validationOptions.maxSize || 5242880)}
            </Typography>
          </div>
        ) : (
          <div className={styles.filePreview}>
            {isImage && preview ? (
              <div className={styles.imagePreview}>
                <img
                  src={preview}
                  alt={value.name}
                  className={styles.previewImage}
                />
              </div>
            ) : (
              <FontAwesomeIcon
                icon={getFileIcon(iconType)}
                className={styles.fileIcon}
              />
            )}

            <div className={styles.fileInfo}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {value.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatFileSize(value.size)}
              </Typography>
            </div>

            {isHovering && (
              <div className={styles.editOverlay}>
                <IconButton
                  className={styles.editButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClick();
                  }}
                  title={t("changeFile")}
                >
                  <FontAwesomeIcon icon={faPencil} />
                </IconButton>
                <IconButton
                  className={styles.editButton}
                  onClick={handleRemove}
                  title={t("removeFile")}
                  sx={{ ml: 1 }}
                >
                  <FontAwesomeIcon icon={faTimesCircle} />
                </IconButton>
              </div>
            )}
          </div>
        )}
      </div>

      {displayError && (
        <Typography
          variant="caption"
          color="error"
          sx={{ mt: 0.5, display: "block" }}
        >
          {validationError || error}
        </Typography>
      )}
    </div>
  );
}